import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import MapView, { Layer, Marker, NavigationControl, Popup, Source } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { buildLocationTelemetry } from "@/lib/location";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Navigation, MapPin, LocateFixed, Volume2, VolumeX, Settings2, Moon, Sun, Play, Pause, Square, Route as RouteIcon, Clock3, X, Fuel, GripHorizontal, AlertTriangle } from "lucide-react";
import EmergencyReportDialog from "@/components/EmergencyReportDialog";
import {
  EMERGENCY_SERVICE_STATUSES,
  EMERGENCY_SERVICE_TYPES,
  formatEmergencyDistance,
  formatEmergencyEta,
  getEmergencyReportStatusBadgeClass,
  getEmergencyReportStatusLabel,
  getEmergencyReportTypeLabel,
  getEmergencyServiceBadgeClass,
  getEmergencyServiceMarkerClass,
  getEmergencyServiceStatusLabel,
  getEmergencyServiceTypeLabel,
  getEmergencySeverityBadgeClass,
  getEmergencySeverityLabel,
  getEmergencyReportMarkerClass,
} from "@/lib/emergency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAP_STYLE_NIGHT = "mapbox://styles/mapbox/navigation-night-v1";
const MAP_STYLE_DAY = "mapbox://styles/mapbox/navigation-day-v1";
const DRIVER_STATUS_KEY = (userId, role = "driver") => role === "emergency" ? `eagle_emergency_last_status_${userId}` : `eagle_driver_last_status_${userId}`;
const GPS_SEARCH_TYPES = "poi,address,street,place,locality,neighborhood,district,postcode";
const GPS_SEARCH_TYPE_PRIORITY = {
  poi: 0,
  address: 1,
  place: 2,
  locality: 3,
  neighborhood: 4,
  district: 5,
  postcode: 6,
};
const GPS_SEARCH_TYPE_LABELS = {
  poi: "Business",
  address: "Address",
  place: "City",
  locality: "Area",
  neighborhood: "Neighborhood",
  district: "District",
  postcode: "ZIP",
};
const GPS_QUICK_SEARCHES = [
  { label: "Food", query: "restaurant" },
  { label: "Fuel", query: "gas station" },
  { label: "Hotel", query: "hotel" },
  { label: "Parking", query: "parking" },
  { label: "Shops", query: "grocery store" },
];

const routeLayer = {
  id: "driver-nav-route",
  type: "line",
  paint: {
    "line-color": "#f97316",
    "line-width": ["interpolate", ["linear"], ["zoom"], 7, 4, 12, 7, 15, 10],
    "line-opacity": 0.95,
  },
};

const toMiles = (meters) => Number((Number(meters || 0) / 1609.344).toFixed(2));
const formatEta = (seconds) => {
  const value = Math.max(0, Math.floor(Number(seconds || 0)));
  const h = Math.floor(value / 3600);
  const m = Math.floor((value % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const haversineMiles = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const r = 3958.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return r * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const minDistanceToRouteMiles = (point, routeCoords) => {
  if (!Array.isArray(routeCoords) || routeCoords.length === 0) return Infinity;
  let min = Infinity;
  for (let i = 0; i < routeCoords.length; i += 4) {
    const [lng, lat] = routeCoords[i];
    const d = haversineMiles(point, { lat, lng });
    if (d < min) min = d;
  }
  return min;
};

const compressRouteCoordinates = (coordinates, maxPoints = 140) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return [];
  if (coordinates.length <= maxPoints) {
    return coordinates
      .map((pair) => [Number(pair?.[0]), Number(pair?.[1])])
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));
  }

  const step = Math.max(1, Math.ceil(coordinates.length / maxPoints));
  const reduced = [];
  for (let i = 0; i < coordinates.length; i += step) {
    const lng = Number(coordinates[i]?.[0]);
    const lat = Number(coordinates[i]?.[1]);
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      reduced.push([lng, lat]);
    }
  }

  const last = coordinates[coordinates.length - 1];
  const lastLng = Number(last?.[0]);
  const lastLat = Number(last?.[1]);
  if (
    Number.isFinite(lastLng) &&
    Number.isFinite(lastLat) &&
    (reduced.length === 0 || reduced[reduced.length - 1][0] !== lastLng || reduced[reduced.length - 1][1] !== lastLat)
  ) {
    reduced.push([lastLng, lastLat]);
  }

  return reduced;
};

const formatSearchDistance = (miles) => {
  if (!Number.isFinite(miles)) return "";
  if (miles < 0.2) return `${Math.round(miles * 5280)} ft`;
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
};

const getSearchFeatureType = (feature) => feature?.properties?.feature_type || feature?.place_type?.[0] || "place";

const getSearchFeatureTitle = (feature) => {
  const type = getSearchFeatureType(feature);
  if (feature?.properties?.name) {
    if (type === "address" && feature?.properties?.address) {
      return `${feature.properties.address} ${feature.properties.name}`;
    }
    return feature.properties.name;
  }
  if (type === "address" && feature?.properties?.address && feature?.text) {
    return `${feature.properties.address} ${feature.text}`;
  }
  return feature?.text || feature?.place_name || "Unknown destination";
};

const getSearchFeatureSubtitle = (feature, title) => {
  const fullAddress = feature?.properties?.full_address || feature?.properties?.place_formatted;
  if (fullAddress) return fullAddress;
  const placeName = String(feature?.place_name || "");
  if (!placeName) return "";
  if (placeName === title) return "";
  if (placeName.startsWith(`${title}, `)) return placeName.slice(title.length + 2);
  const parts = placeName.split(", ");
  if (parts.length > 1) return parts.slice(1).join(", ");
  return placeName;
};

const getSearchFeatureCategory = (feature) => {
  const rawCategory = Array.isArray(feature?.properties?.poi_category)
    ? feature.properties.poi_category[0]
    : feature?.properties?.category || feature?.properties?.maki || "";
  return String(rawCategory).split(",")[0]?.trim() || "";
};

const enrichSearchFeature = (feature, currentPos, query) => {
  const type = getSearchFeatureType(feature);
  const title = getSearchFeatureTitle(feature);
  const subtitle = getSearchFeatureSubtitle(feature, title);
  const category = getSearchFeatureCategory(feature);
  const coords = Array.isArray(feature?.geometry?.coordinates)
    ? feature.geometry.coordinates
    : Array.isArray(feature?.center)
      ? feature.center
      : null;
  const distanceMiles =
    currentPos && coords && Number.isFinite(coords[0]) && Number.isFinite(coords[1])
      ? haversineMiles(currentPos, { lng: Number(coords[0]), lat: Number(coords[1]) })
      : null;
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const searchable = `${title} ${subtitle} ${category}`.toLowerCase();
  const queryPrefixBonus = normalizedQuery && searchable.startsWith(normalizedQuery) ? -30 : 0;
  const queryIncludesBonus = normalizedQuery && searchable.includes(normalizedQuery) ? -10 : 0;
  const categoryBonus = normalizedQuery && category.toLowerCase().includes(normalizedQuery) ? -8 : 0;
  const typePriority = GPS_SEARCH_TYPE_PRIORITY[type] ?? 10;
  const distancePenalty = Number.isFinite(distanceMiles) ? Math.min(distanceMiles, 200) / 4 : 18;

  return {
    ...feature,
    center: coords || feature?.center || null,
    place_name: feature?.properties?.full_address || feature?.place_name || title,
    featureType: type,
    featureTypeLabel: GPS_SEARCH_TYPE_LABELS[type] || "Place",
    primaryLabel: title,
    secondaryLabel: subtitle,
    categoryLabel: category,
    distanceMiles,
    rankingScore: (typePriority * 28) + distancePenalty + queryPrefixBonus + queryIncludesBonus + categoryBonus,
  };
};

const DriverNavigationConsole = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [launcherHidden, setLauncherHidden] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [destinationQuery, setDestinationQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [destination, setDestination] = useState(null);
  const [currentPos, setCurrentPos] = useState(null);
  const [routeGeo, setRouteGeo] = useState(null);
  const [steps, setSteps] = useState([]);
  const [tripStatus, setTripStatus] = useState("idle");
  const [distanceRemainingMiles, setDistanceRemainingMiles] = useState(0);
  const [etaSeconds, setEtaSeconds] = useState(0);
  const [speedMph, setSpeedMph] = useState(0);
  const [progressPct, setProgressPct] = useState(0);
  const [initialDistanceMiles, setInitialDistanceMiles] = useState(null);
  const [followMode, setFollowMode] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [muted, setMuted] = useState(false);
  const [autoRecenter, setAutoRecenter] = useState(true);
  const [avoidTolls, setAvoidTolls] = useState(false);
  const [avoidHighways, setAvoidHighways] = useState(false);
  const [units, setUnits] = useState("mi");
  const [nightMode, setNightMode] = useState(true);
  const [viewState, setViewState] = useState({ longitude: -98.5795, latitude: 39.8283, zoom: 4, pitch: 0, bearing: 0 });
  const [controlsHidden, setControlsHidden] = useState(false);
  const [showTruckStops, setShowTruckStops] = useState(true);
  const [truckStops, setTruckStops] = useState([]);
  const [bannerOffset, setBannerOffset] = useState({ x: 0, y: 0 });
  const [launcherOffset, setLauncherOffset] = useState({ x: 0, y: 0 });
  const [emergencyServices, setEmergencyServices] = useState([]);
  const [emergencyReports, setEmergencyReports] = useState([]);
  const [emergencyPanelOpen, setEmergencyPanelOpen] = useState(false);
  const [showEmergencyServices, setShowEmergencyServices] = useState(true);
  const [showEmergencyReports, setShowEmergencyReports] = useState(true);
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencyError, setEmergencyError] = useState("");
  const [emergencyRouteLoading, setEmergencyRouteLoading] = useState(false);
  const [emergencyRouteError, setEmergencyRouteError] = useState("");
  const [emergencyServiceFilter, setEmergencyServiceFilter] = useState("all");
  const [emergencyStatusFilter, setEmergencyStatusFilter] = useState("all");
  const [safeRouteMode, setSafeRouteMode] = useState(true);
  const [emergencyRoutePreference, setEmergencyRoutePreference] = useState("nearest");
  const [selectedEmergencyFeature, setSelectedEmergencyFeature] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [placingEmergencyReportPin, setPlacingEmergencyReportPin] = useState(false);
  const [reportPinCoords, setReportPinCoords] = useState(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [emergencyReportForm, setEmergencyReportForm] = useState({
    report_type: "medical",
    severity: "high",
    description: "",
  });
  const [activeEmergencyRoute, setActiveEmergencyRoute] = useState(null);

  const watchIdRef = useRef(null);
  const mapRef = useRef(null);
  const lastRouteFetchRef = useRef(0);
  const lastSpokenInstructionRef = useRef("");
  const rerouteNoticeRef = useRef(0);
  const lastTruckStopFetchRef = useRef(0);
  const bannerDragRef = useRef(null);
  const launcherDragRef = useRef(null);
  const manualCameraUntilRef = useRef(0);
  const lastEmergencyFetchRef = useRef(0);
  const searchRequestRef = useRef(0);
  const [lockToHeading, setLockToHeading] = useState(true);

  const statusText = useMemo(() => {
    if (tripStatus === "active") return "Navigating";
    if (tripStatus === "paused") return "Paused";
    if (tripStatus === "arrived") return "Arrived";
    return "Start Navigation";
  }, [tripStatus]);

  const nextTurn = steps?.[0]?.maneuver?.instruction || "";
  const isNavigating = tripStatus === "active";
  const canSyncAdmin = ["driver", "emergency"].includes(user?.role) && !!user?.id;
  const clampBannerOffset = useCallback((nextX, nextY) => {
    const maxX = Math.max(80, window.innerWidth * 0.42);
    const maxUp = -Math.max(0, window.innerHeight * 0.04);
    const maxDown = Math.max(0, window.innerHeight * 0.65);

    return {
      x: Math.max(-maxX, Math.min(maxX, nextX)),
      y: Math.max(maxUp, Math.min(maxDown, nextY)),
    };
  }, []);
  const clampLauncherOffset = useCallback((nextX, nextY) => {
    const maxX = Math.max(80, window.innerWidth * 0.42);
    const maxUp = -Math.max(0, window.innerHeight * 0.55);
    const maxDown = Math.max(0, window.innerHeight * 0.03);

    return {
      x: Math.max(-maxX, Math.min(maxX, nextX)),
      y: Math.max(maxUp, Math.min(maxDown, nextY)),
    };
  }, []);
  const launcherActionText = useMemo(() => {
    if (tripStatus === "active") return "Open Trip";
    if (tripStatus === "paused" && destination && currentPos) return "Resume";
    if (destination && currentPos) return "Start Navigation";
    return "Open GPS";
  }, [currentPos, destination, tripStatus]);
  const filteredEmergencyServices = useMemo(
    () =>
      emergencyServices.filter((service) => {
        const typeMatches = emergencyServiceFilter === "all" || service.service_type === emergencyServiceFilter;
        const statusMatches = emergencyStatusFilter === "all" || service.status === emergencyStatusFilter;
        return typeMatches && statusMatches;
      }),
    [emergencyServiceFilter, emergencyServices, emergencyStatusFilter],
  );
  const activeEmergencyReports = useMemo(
    () => emergencyReports.filter((report) => report.status !== "resolved"),
    [emergencyReports],
  );

  const speak = useCallback((text) => {
    if (!text || !voiceEnabled || muted || typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 1;
    utter.pitch = 1;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
  }, [muted, voiceEnabled]);

  const syncTripToAdmin = useCallback(async ({ current, distanceMiles, etaSec, nextInstruction, progress, status, routeCoordinates = null, destinationPoint = destination }) => {
    if (!canSyncAdmin || !current) return;
    const rememberedStatus = localStorage.getItem(DRIVER_STATUS_KEY(user.id, user?.role)) || user?.driver_status || "off_duty";
    const normalizedRoute = routeCoordinates === null
      ? compressRouteCoordinates(routeGeo?.geometry?.coordinates || [])
      : compressRouteCoordinates(routeCoordinates || []);
    try {
      const locationPayload = await buildLocationTelemetry({
        latitude: current.lat,
        longitude: current.lng,
        lat: current.lat,
        lng: current.lng,
        heading: current.heading,
        speed_mph: Number(speedMph.toFixed(1)),
        tracked_at: new Date().toISOString(),
        trip_status: status,
        destination_name: destinationPoint?.place_name || destinationPoint?.name || null,
        destination_lat: destinationPoint?.center?.[1] ?? destinationPoint?.lat ?? null,
        destination_lng: destinationPoint?.center?.[0] ?? destinationPoint?.lng ?? null,
        eta_seconds: Math.max(0, Math.floor(etaSec || 0)),
        eta_at: etaSec ? new Date(Date.now() + Number(etaSec) * 1000).toISOString() : null,
        distance_remaining_miles: Number(distanceMiles || 0),
        route_progress_pct: Number(progress || 0),
        next_turn_instruction: nextInstruction || null,
        route_geometry: normalizedRoute.length
          ? { type: "LineString", coordinates: normalizedRoute }
          : null,
        route_updated_at: new Date().toISOString(),
      });
      await axios.put(`${API}/users/driver-status`, {
        status: rememberedStatus,
        location: locationPayload,
      });
    } catch (_) {
      // Non-blocking sync.
    }
  }, [canSyncAdmin, destination, routeGeo, speedMph, user]);

  const fetchEmergencyMapData = useCallback(async ({ force = false } = {}) => {
    if (!currentPos) return;
    const now = Date.now();
    if (!force && now - lastEmergencyFetchRef.current < 60000) return;
    lastEmergencyFetchRef.current = now;
    setEmergencyLoading(true);
    setEmergencyError("");
    try {
      const response = await axios.get(`${API}/emergency/map-data`, {
        params: {
          near_latitude: currentPos.lat,
          near_longitude: currentPos.lng,
          radius_miles: 120,
        },
      });
      setEmergencyServices(response.data?.services || []);
      setEmergencyReports(response.data?.reports || []);
    } catch (error) {
      setEmergencyError(error.response?.data?.detail || "Could not load emergency overlays");
    } finally {
      setEmergencyLoading(false);
    }
  }, [currentPos]);

  const applyRouteSnapshot = useCallback(async ({
    origin,
    destinationPoint,
    coordinates,
    navSteps,
    remainingMiles,
    eta,
    statusOverride = tripStatus,
    announceReroute = false,
    routeMeta = null,
  }) => {
    setRouteGeo({ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates } });
    setSteps(navSteps || []);
    setDistanceRemainingMiles(remainingMiles);
    setEtaSeconds(eta);
    if (routeMeta) setActiveEmergencyRoute(routeMeta);

    let nextProgress = progressPct;
    if (initialDistanceMiles === null || initialDistanceMiles <= 0 || tripStatus === "idle") {
      setInitialDistanceMiles(Math.max(remainingMiles, 0.01));
      nextProgress = 0;
      setProgressPct(0);
    } else {
      const p = Math.min(100, Math.max(0, (1 - remainingMiles / initialDistanceMiles) * 100));
      nextProgress = Number(p.toFixed(1));
      setProgressPct(nextProgress);
    }

    const instruction = navSteps?.[0]?.maneuver?.instruction || "";
    if (instruction && instruction !== lastSpokenInstructionRef.current && tripStatus === "active") {
      lastSpokenInstructionRef.current = instruction;
      speak(instruction);
    }

    if (announceReroute) {
      speak("Route updated. Continuing navigation.");
    }

    await syncTripToAdmin({
      current: origin,
      distanceMiles: remainingMiles,
      etaSec: eta,
      nextInstruction: instruction,
      progress: nextProgress,
      status: statusOverride,
      routeCoordinates: coordinates,
      destinationPoint,
    });
  }, [initialDistanceMiles, progressPct, speak, syncTripToAdmin, tripStatus]);

  const fetchRoute = useCallback(async ({ origin, destinationPoint, announceReroute = false, statusOverride = tripStatus }) => {
    if (!origin || !destinationPoint || !MAPBOX_TOKEN) return;
    if (destinationPoint?.emergency_service_id) {
      const response = await axios.post(`${API}/emergency/routes/generate`, {
        origin_latitude: origin.lat,
        origin_longitude: origin.lng,
        service_id: destinationPoint.emergency_service_id,
        preference: emergencyRoutePreference,
        safe_route: safeRouteMode,
      });
      const payload = response.data;
      const coords = payload?.route_geometry?.coordinates || [];
      const navSteps = payload?.steps || [];
      const remainingMiles = Number(payload?.distance_miles || 0);
      const eta = Number(payload?.duration_seconds || 0);
      await applyRouteSnapshot({
        origin,
        destinationPoint,
        coordinates: coords,
        navSteps,
        remainingMiles,
        eta,
        statusOverride,
        announceReroute,
        routeMeta: payload,
      });
      return;
    }
    const originLng = Number(origin.lng);
    const originLat = Number(origin.lat);
    const destLng = Number(destinationPoint.center?.[0] ?? destinationPoint.lng);
    const destLat = Number(destinationPoint.center?.[1] ?? destinationPoint.lat);

    const exclude = [];
    if (avoidTolls) exclude.push("toll");
    if (avoidHighways) exclude.push("motorway");
    const excludeParam = exclude.length ? `&exclude=${exclude.join(",")}` : "";

    const url = `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${originLng},${originLat};${destLng},${destLat}?overview=full&geometries=geojson&steps=true&voice_instructions=true&banner_instructions=true${excludeParam}&access_token=${MAPBOX_TOKEN}`;
    const response = await fetch(url);
    if (!response.ok) return;
    const payload = await response.json();
    const route = payload?.routes?.[0];
    if (!route) return;

    const coords = route.geometry?.coordinates || [];
    const legs = route.legs || [];
    const navSteps = legs[0]?.steps || [];
    const remainingMiles = toMiles(route.distance);
    const eta = Number(route.duration || 0);
    await applyRouteSnapshot({
      origin,
      destinationPoint,
      coordinates: coords,
      navSteps,
      remainingMiles,
      eta,
      statusOverride,
      announceReroute,
      routeMeta: null,
    });
  }, [applyRouteSnapshot, avoidHighways, avoidTolls, emergencyRoutePreference, safeRouteMode, tripStatus]);

  useEffect(() => {
    if (!navigator.geolocation) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const coords = position.coords;
        const point = {
          lat: coords.latitude,
          lng: coords.longitude,
          heading: coords.heading,
        };
        setCurrentPos(point);
        if (typeof coords.speed === "number" && coords.speed >= 0) {
          setSpeedMph(coords.speed * 2.23694);
        }

        const firstPerson = tripStatus === "active";
        const manualCameraActive = Date.now() < manualCameraUntilRef.current;
        const allowAutoCenter = !manualCameraActive && (autoRecenter || (followMode && !firstPerson) || (firstPerson && lockToHeading));
        if (allowAutoCenter) {
          const nextBearing = typeof point.heading === "number" ? point.heading : 0;
          setViewState((prev) => ({
            ...prev,
            latitude: point.lat,
            longitude: point.lng,
            zoom: firstPerson ? Math.max(prev.zoom, 15.5) : Math.max(prev.zoom, 11),
            pitch: firstPerson ? 62 : Math.max(prev.pitch || 0, 0),
            bearing: firstPerson && lockToHeading ? nextBearing : prev.bearing || 0,
          }));
          const map = mapRef.current?.getMap?.();
          if (map && firstPerson && lockToHeading) {
            map.easeTo({
              center: [point.lng, point.lat],
              zoom: Math.max(map.getZoom(), 15.5),
              pitch: 62,
              bearing: nextBearing,
              duration: 650,
            });
          }
        }

        const now = Date.now();
        const shouldRefreshRoute = tripStatus === "active" && destination && now - lastRouteFetchRef.current > 8000;
        if (shouldRefreshRoute) {
          lastRouteFetchRef.current = now;

          const routeCoords = routeGeo?.geometry?.coordinates || [];
          const offRouteMiles = minDistanceToRouteMiles(point, routeCoords);
          const isOffRoute = Number.isFinite(offRouteMiles) && offRouteMiles > 0.35;
          if (isOffRoute && now - rerouteNoticeRef.current > 12000) {
            rerouteNoticeRef.current = now;
            speak("Rerouting due to route deviation.");
            fetchRoute({ origin: point, destinationPoint: destination, announceReroute: true });
          } else {
            fetchRoute({ origin: point, destinationPoint: destination });
          }
        }
      },
      () => {
        // Silent for continuous UX.
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 10000,
      },
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [destination, tripStatus, followMode, autoRecenter, routeGeo, speedMph, lockToHeading, fetchRoute, speak]);

  useEffect(() => {
    if (isNavigating) {
      setSuggestions([]);
      setSearchLoading(false);
      setSearchError("");
      return;
    }
    if (!destinationQuery || destinationQuery.trim().length < 2 || !MAPBOX_TOKEN) {
      setSuggestions([]);
      setSearchLoading(false);
      setSearchError("");
      return;
    }
    const currentRequestId = searchRequestRef.current + 1;
    searchRequestRef.current = currentRequestId;
    const timeoutId = setTimeout(async () => {
      try {
        setSearchLoading(true);
        setSearchError("");
        const proximity = currentPos ? `&proximity=${currentPos.lng},${currentPos.lat}` : "";
        const origin = currentPos ? `&origin=${currentPos.lng},${currentPos.lat}` : "";
        const query = destinationQuery.trim();
        const url = `https://api.mapbox.com/search/searchbox/v1/forward?q=${encodeURIComponent(query)}&limit=8&types=${GPS_SEARCH_TYPES}&language=en&country=US&navigation_profile=driving&eta_type=navigation${proximity}${origin}&access_token=${MAPBOX_TOKEN}`;
        const response = await fetch(url);
        if (!response.ok) {
          if (searchRequestRef.current === currentRequestId) {
            setSuggestions([]);
            setSearchError(response.status === 401 || response.status === 403 ? "Business search is not enabled for this Mapbox token" : "Search is unavailable right now");
          }
          return;
        }
        const payload = await response.json();
        if (searchRequestRef.current !== currentRequestId) return;
        const nextSuggestions = Array.isArray(payload?.features)
          ? payload.features
              .map((feature) => enrichSearchFeature(feature, currentPos, query))
              .sort((a, b) => a.rankingScore - b.rankingScore)
              .slice(0, 8)
          : [];
        setSuggestions(nextSuggestions);
        setSearchError(nextSuggestions.length === 0 ? "No nearby businesses or places matched your search" : "");
      } catch (_) {
        if (searchRequestRef.current === currentRequestId) {
          setSuggestions([]);
          setSearchError("Search is unavailable right now");
        }
      } finally {
        if (searchRequestRef.current === currentRequestId) {
          setSearchLoading(false);
        }
      }
    }, 280);
    return () => clearTimeout(timeoutId);
  }, [destinationQuery, currentPos, isNavigating]);

  useEffect(() => {
    if (location.pathname === "/navigation") {
      setOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("eagle-open-gps", openHandler);
    return () => window.removeEventListener("eagle-open-gps", openHandler);
  }, []);

  useEffect(() => {
    if (!showTruckStops || !currentPos || !MAPBOX_TOKEN) {
      if (!showTruckStops) setTruckStops([]);
      return;
    }
    const now = Date.now();
    if (now - lastTruckStopFetchRef.current < 90000) return;
    lastTruckStopFetchRef.current = now;

    const fetchTruckStops = async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent("truck stop")}.json?proximity=${currentPos.lng},${currentPos.lat}&limit=20&types=poi&access_token=${MAPBOX_TOKEN}`;
        const response = await fetch(url);
        if (!response.ok) return;
        const payload = await response.json();
        setTruckStops(payload?.features || []);
      } catch (_) {
        // Keep non-blocking.
      }
    };
    fetchTruckStops();
  }, [currentPos, showTruckStops]);

  useEffect(() => {
    if (!open || !currentPos) return;
    fetchEmergencyMapData();
  }, [currentPos, fetchEmergencyMapData, open]);

  useEffect(() => {
    if (tripStatus !== "active") return;
    if (distanceRemainingMiles <= 0.1 && destination) {
      setTripStatus("arrived");
      setProgressPct(100);
      speak("You have arrived at your destination.");
      syncTripToAdmin({
        current: currentPos,
        distanceMiles: 0,
        etaSec: 0,
        nextInstruction: "Arrived",
        progress: 100,
        status: "arrived",
        destinationPoint: destination,
      });
    }
  }, [tripStatus, distanceRemainingMiles, destination, currentPos, speak, syncTripToAdmin]);

  const selectDestination = async (item) => {
    setActiveEmergencyRoute(item?.emergency_service_id ? activeEmergencyRoute : null);
    setDestination(item);
    setDestinationQuery(item.primaryLabel || item.place_name || "");
    setSuggestions([]);
    setSearchLoading(false);
    setSearchError("");
    if (currentPos) {
      await fetchRoute({ origin: currentPos, destinationPoint: item, statusOverride: tripStatus });
    }
  };

  const runQuickSearch = (query) => {
    setDestinationQuery(query);
    setSuggestions([]);
    setSearchError("");
    setSearchLoading(false);
  };

  const startTrip = async () => {
    if (!currentPos || !destination) return;
    setOpen(true);
    setSuggestions([]);
    setControlsHidden(false);
    setLockToHeading(true);
    manualCameraUntilRef.current = 0;
    setTripStatus("active");
    await fetchRoute({ origin: currentPos, destinationPoint: destination, statusOverride: "active" });
    speak("Trip started. Navigation is now active.");
  };

  const pauseTrip = async () => {
    setTripStatus("paused");
    await syncTripToAdmin({
      current: currentPos,
      distanceMiles: distanceRemainingMiles,
      etaSec: etaSeconds,
      nextInstruction: nextTurn,
      progress: progressPct,
      status: "paused",
      destinationPoint: destination,
    });
  };

  const resumeTrip = async () => {
    setTripStatus("active");
    setControlsHidden(false);
    setLockToHeading(true);
    manualCameraUntilRef.current = 0;
    if (currentPos && destination) {
      await fetchRoute({ origin: currentPos, destinationPoint: destination, statusOverride: "active" });
    }
    speak("Trip resumed.");
  };

  const endTrip = async () => {
    setTripStatus("ended");
    await syncTripToAdmin({
      current: currentPos,
      distanceMiles: 0,
      etaSec: 0,
      nextInstruction: "Trip ended",
      progress: 100,
      status: "ended",
      routeCoordinates: [],
      destinationPoint: null,
    });
    setDestination(null);
    setDestinationQuery("");
    setRouteGeo(null);
    setSteps([]);
    setDistanceRemainingMiles(0);
    setEtaSeconds(0);
    setProgressPct(0);
    setInitialDistanceMiles(null);
    setControlsHidden(false);
    setLockToHeading(true);
    setActiveEmergencyRoute(null);
    manualCameraUntilRef.current = 0;
    lastSpokenInstructionRef.current = "";
  };

  const recenter = () => {
    if (!currentPos) return;
    const firstPerson = tripStatus === "active";
    setLockToHeading(true);
    manualCameraUntilRef.current = 0;
    setViewState((prev) => ({
      ...prev,
      latitude: currentPos.lat,
      longitude: currentPos.lng,
      zoom: Math.max(prev.zoom, firstPerson ? 15.5 : 12),
      pitch: firstPerson ? 62 : prev.pitch || 0,
      bearing: firstPerson ? Number(currentPos.heading || 0) : prev.bearing || 0,
    }));
    const map = mapRef.current?.getMap?.();
    if (map) {
      map.easeTo({
        center: [currentPos.lng, currentPos.lat],
        zoom: Math.max(map.getZoom(), firstPerson ? 15.5 : 12),
        pitch: firstPerson ? 62 : map.getPitch(),
        bearing: firstPerson ? Number(currentPos.heading || map.getBearing()) : map.getBearing(),
        duration: 500,
      });
    }
  };

  const shownDistance = units === "km" ? Number((distanceRemainingMiles * 1.60934).toFixed(2)) : distanceRemainingMiles;
  const distanceLabel = units === "km" ? "km" : "mi";
  const handleLauncherAction = async () => {
    if (tripStatus === "active") {
      setOpen(true);
      return;
    }
    if (tripStatus === "paused" && currentPos && destination) {
      setOpen(true);
      await resumeTrip();
      return;
    }
    if (currentPos && destination) {
      setOpen(true);
      await startTrip();
      return;
    }
    setOpen(true);
  };
  const updateEmergencyReportForm = (field, value) => {
    setEmergencyReportForm((prev) => ({ ...prev, [field]: value }));
  };

  const useCurrentLocationForReport = () => {
    if (!currentPos) {
      toast.error("Current location is not available yet");
      return;
    }
    setReportPinCoords({ latitude: currentPos.lat, longitude: currentPos.lng });
    setPlacingEmergencyReportPin(false);
  };

  const resetEmergencyReport = () => {
    setEmergencyReportForm({ report_type: "medical", severity: "high", description: "" });
    setReportPinCoords(null);
    setPlacingEmergencyReportPin(false);
  };

  const submitEmergencyReport = async () => {
    if (!user) {
      toast.error("Sign in to submit and save emergency reports");
      return;
    }
    const latitude = Number(reportPinCoords?.latitude ?? currentPos?.lat);
    const longitude = Number(reportPinCoords?.longitude ?? currentPos?.lng);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      toast.error("Select coordinates on the map or use your current location");
      return;
    }
    setReportSubmitting(true);
    try {
      const response = await axios.post(`${API}/emergency/reports`, {
        report_type: emergencyReportForm.report_type,
        severity: emergencyReportForm.severity,
        description: emergencyReportForm.description.trim(),
        latitude,
        longitude,
      });
      const report = response.data;
      setEmergencyReports((prev) => [report, ...prev]);
      setSelectedEmergencyFeature({ ...report, feature_kind: "report" });
      setReportDialogOpen(false);
      resetEmergencyReport();
      toast.success("Emergency report saved");
      fetchEmergencyMapData({ force: true });
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      const detail = error?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (status >= 500) {
        toast.error("The server could not save the emergency report");
      } else {
        toast.error(error.message || "Could not submit emergency report");
      }
    } finally {
      setReportSubmitting(false);
    }
  };

  const generateEmergencyRoute = useCallback(
    async ({ preference = emergencyRoutePreference, serviceId = null, reportId = null, serviceTypeOverride = null, safeRouteOverride = safeRouteMode } = {}) => {
      if (!user) {
        toast.error("Sign in to generate and save emergency routes");
        return;
      }
      if (!currentPos) {
        toast.error("Current location is required before routing to emergency services");
        return;
      }
      setEmergencyRouteLoading(true);
      setEmergencyRouteError("");
      try {
        const response = await axios.post(`${API}/emergency/routes/generate`, {
          origin_latitude: currentPos.lat,
          origin_longitude: currentPos.lng,
          service_id: serviceId || undefined,
          report_id: reportId || undefined,
          service_type: serviceId ? undefined : (serviceTypeOverride || (emergencyServiceFilter !== "all" ? emergencyServiceFilter : "hospital")),
          preference,
          safe_route: safeRouteOverride,
        });
        const payload = response.data;
        const service = payload?.service;
        if (!service) {
          throw new Error("Emergency service route response was incomplete");
        }
        setEmergencyRoutePreference(preference);
        setDestination({
          ...service,
          emergency_service_id: service.id,
          place_name: service.address || service.name,
          center: [Number(service.longitude), Number(service.latitude)],
          name: service.name,
        });
        setDestinationQuery(service.name || service.address || "");
        setSuggestions([]);
        await applyRouteSnapshot({
          origin: currentPos,
          destinationPoint: {
            ...service,
            emergency_service_id: service.id,
            place_name: service.address || service.name,
            center: [Number(service.longitude), Number(service.latitude)],
            name: service.name,
          },
          coordinates: payload?.route_geometry?.coordinates || [],
          navSteps: payload?.steps || [],
          remainingMiles: Number(payload?.distance_miles || 0),
          eta: Number(payload?.duration_seconds || 0),
          statusOverride: tripStatus,
          routeMeta: payload,
        });
        setOpen(true);
        toast.success(`Emergency route ready: ${service.name}`);
      } catch (error) {
        const message = error.response?.data?.detail || error.message || "Could not generate emergency route";
        setEmergencyRouteError(message);
        toast.error(message);
      } finally {
        setEmergencyRouteLoading(false);
      }
    },
    [applyRouteSnapshot, currentPos, emergencyRoutePreference, emergencyServiceFilter, safeRouteMode, tripStatus, user],
  );

  useEffect(() => {
    const actionHandler = async (event) => {
      const detail = event?.detail || {};
      setOpen(true);
      setEmergencyPanelOpen(true);
      if (typeof detail.safeMode === "boolean") {
        setSafeRouteMode(detail.safeMode);
      }

      if (detail.action === "report-incident") {
        setReportDialogOpen(true);
        return;
      }

      if (detail.action === "open-tools" || !detail.action) {
        return;
      }

      if (!currentPos) {
        toast.error("Current location is required before opening emergency routing");
        return;
      }

      await fetchEmergencyMapData({ force: true });

      if (detail.action === "route-report" && detail.reportId) {
        await generateEmergencyRoute({ reportId: detail.reportId, safeRouteOverride: detail.safeMode });
        return;
      }

      if (detail.action === "route-service" && detail.serviceId) {
        await generateEmergencyRoute({ serviceId: detail.serviceId, safeRouteOverride: detail.safeMode });
      }
    };

    window.addEventListener("eagle-emergency-action", actionHandler);
    return () => window.removeEventListener("eagle-emergency-action", actionHandler);
  }, [currentPos, fetchEmergencyMapData, generateEmergencyRoute]);

  const beginBannerDrag = (event) => {
    bannerDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: bannerOffset.x,
      originY: bannerOffset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveBannerDrag = (event) => {
    const drag = bannerDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    setBannerOffset(clampBannerOffset(drag.originX + dx, drag.originY + dy));
  };

  const endBannerDrag = (event) => {
    const drag = bannerDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    bannerDragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const beginLauncherDrag = (event) => {
    launcherDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: launcherOffset.x,
      originY: launcherOffset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveLauncherDrag = (event) => {
    const drag = launcherDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    setLauncherOffset(clampLauncherOffset(drag.originX + dx, drag.originY + dy));
  };

  const endLauncherDrag = (event) => {
    const drag = launcherDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    launcherDragRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <>
      {launcherHidden ? (
        <div className="fixed bottom-20 sm:bottom-24 right-3 z-[85]">
          <Button
            size="sm"
            variant="outline"
            className="border-orange-500/40 bg-black/85 text-orange-100 backdrop-blur"
            onClick={() => setLauncherHidden(false)}
          >
            <Navigation className="w-4 h-4 mr-1 text-orange-400" />
            GPS
          </Button>
        </div>
      ) : (
        <div
          className="fixed bottom-20 sm:bottom-24 left-1/2 z-[85] w-[min(92vw,760px)]"
          style={{ transform: `translate(calc(-50% + ${launcherOffset.x}px), ${launcherOffset.y}px)` }}
        >
          <div className="w-full rounded-xl border border-orange-500/40 bg-black/85 px-4 py-3 text-left text-orange-100 shadow-[0_0_24px_rgba(249,115,22,0.25)] backdrop-blur">
            <div
              className="mb-2 flex items-center justify-center gap-1 text-[11px] text-stone-400 select-none cursor-grab active:cursor-grabbing"
              onPointerDown={beginLauncherDrag}
              onPointerMove={moveLauncherDrag}
              onPointerUp={endLauncherDrag}
              onPointerCancel={endLauncherDrag}
              title="Drag GPS banner"
              style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
            >
              <GripHorizontal className="w-3.5 h-3.5" />
              <span>Drag</span>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button type="button" className="min-w-0 flex-1 text-left" onClick={handleLauncherAction}>
                <p className="text-[11px] uppercase tracking-wider text-orange-300">GPS Navigation</p>
                <p className="text-sm truncate font-medium">{destination?.place_name || "Open GPS and choose destination"}</p>
              </button>
              <div className="flex items-center gap-2 text-xs text-stone-300">
                <Navigation className="w-4 h-4 text-orange-400" />
                <span>{statusText}</span>
                {tripStatus === "active" && <span>· ETA {formatEta(etaSeconds)}</span>}
              </div>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-500" onClick={handleLauncherAction}>
                {launcherActionText}
              </Button>
              <Button size="icon" variant="outline" className="h-8 w-8 border-stone-700 bg-black/70" onClick={() => setLauncherHidden(true)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[95] bg-black text-stone-100">
          <MapView
            ref={mapRef}
            mapboxAccessToken={MAPBOX_TOKEN}
            mapLib={mapboxgl}
            mapStyle={nightMode ? MAP_STYLE_NIGHT : MAP_STYLE_DAY}
            latitude={viewState.latitude}
            longitude={viewState.longitude}
            zoom={viewState.zoom}
            pitch={viewState.pitch}
            bearing={viewState.bearing}
            dragRotate
            pitchWithRotate
            touchZoomRotate
            onMove={(evt) => {
              setViewState(evt.viewState);
              if (!autoRecenter) setFollowMode(false);
            }}
                    onDragStart={() => {
                      if (isNavigating) {
                        manualCameraUntilRef.current = Date.now() + 60000;
                        setLockToHeading(false);
                        setFollowMode(false);
                      }
                    }}
                    onRotateStart={() => {
                      if (isNavigating) {
                        manualCameraUntilRef.current = Date.now() + 60000;
                        setLockToHeading(false);
                        setFollowMode(false);
                      }
                    }}
            onPitchStart={() => {
              if (isNavigating) {
                manualCameraUntilRef.current = Date.now() + 60000;
                setLockToHeading(false);
                setFollowMode(false);
              }
            }}
            onClick={(evt) => {
              if (!placingEmergencyReportPin) return;
              setReportPinCoords({ latitude: evt.lngLat.lat, longitude: evt.lngLat.lng });
              setPlacingEmergencyReportPin(false);
              setReportDialogOpen(true);
            }}
            style={{ width: "100vw", height: "100vh" }}
          >
            <NavigationControl position="top-left" showCompass style={{ marginTop: "45vh", pointerEvents: "auto" }} />
            {routeGeo && (
              <Source id="route-source-driver" type="geojson" data={routeGeo}>
                <Layer {...routeLayer} />
              </Source>
            )}
            {currentPos && (
              <Marker longitude={currentPos.lng} latitude={currentPos.lat} rotationAlignment="map" rotation={Number(currentPos.heading || 0)}>
                <div className="w-4 h-4 rounded-full border-2 border-white bg-orange-500 shadow-[0_0_14px_rgba(249,115,22,0.9)]" />
              </Marker>
            )}
            {destination && (
              <Marker longitude={Number(destination.center?.[0] ?? destination.lng)} latitude={Number(destination.center?.[1] ?? destination.lat)}>
                <div className="w-3.5 h-3.5 rounded-full border border-white bg-emerald-500" />
              </Marker>
            )}
            {showTruckStops && truckStops.map((stop) => (
              <Marker
                key={stop.id}
                longitude={Number(stop.center?.[0])}
                latitude={Number(stop.center?.[1])}
                anchor="bottom"
              >
                <div className="relative">
                  <div className="w-8 h-8 rotate-45 rounded-md bg-amber-500 border-2 border-black shadow-[0_0_12px_rgba(245,158,11,0.7)]" />
                  <div className="absolute inset-0 flex items-center justify-center -rotate-45 text-black">
                    <Fuel className="w-4 h-4" />
                  </div>
                </div>
              </Marker>
            ))}
            {showEmergencyServices && filteredEmergencyServices.map((service) => (
              <Marker
                key={service.id}
                longitude={Number(service.longitude)}
                latitude={Number(service.latitude)}
                anchor="center"
              >
                <button
                  type="button"
                  className={getEmergencyServiceMarkerClass(service.service_type, service.status)}
                  onClick={() => setSelectedEmergencyFeature({ ...service, feature_kind: "service" })}
                  title={service.name}
                >
                  <span className="text-[10px] font-bold">{getEmergencyServiceTypeLabel(service.service_type).slice(0, 1)}</span>
                </button>
              </Marker>
            ))}
            {showEmergencyReports && activeEmergencyReports.map((report) => (
              <Marker
                key={report.id}
                longitude={Number(report.longitude)}
                latitude={Number(report.latitude)}
                anchor="center"
              >
                <button
                  type="button"
                  className={getEmergencyReportMarkerClass(report.severity, report.status)}
                  onClick={() => setSelectedEmergencyFeature({ ...report, feature_kind: "report" })}
                  title={report.description}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                </button>
              </Marker>
            ))}
            {selectedEmergencyFeature && (
              <Popup
                longitude={Number(selectedEmergencyFeature.longitude)}
                latitude={Number(selectedEmergencyFeature.latitude)}
                anchor="bottom"
                offset={20}
                closeOnClick={false}
                onClose={() => setSelectedEmergencyFeature(null)}
              >
                <div className="min-w-[230px] space-y-3 p-2 text-xs text-stone-900">
                  {selectedEmergencyFeature.feature_kind === "service" ? (
                    <>
                      <div>
                        <p className="text-sm font-semibold">{selectedEmergencyFeature.name}</p>
                        <p className="text-stone-600">{getEmergencyServiceTypeLabel(selectedEmergencyFeature.service_type)}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className={getEmergencyServiceBadgeClass(selectedEmergencyFeature.status)}>
                          {getEmergencyServiceStatusLabel(selectedEmergencyFeature.status)}
                        </Badge>
                        {selectedEmergencyFeature.distance_miles !== undefined && (
                          <Badge variant="outline">{formatEmergencyDistance(selectedEmergencyFeature.distance_miles)}</Badge>
                        )}
                      </div>
                      <p>{selectedEmergencyFeature.address}</p>
                      <Button
                        size="sm"
                        className="w-full bg-orange-600 hover:bg-orange-500"
                        disabled={emergencyRouteLoading}
                        onClick={() => generateEmergencyRoute({ serviceId: selectedEmergencyFeature.id })}
                      >
                        {emergencyRouteLoading ? "Routing..." : "Route To Service"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div>
                        <p className="text-sm font-semibold">{getEmergencyReportTypeLabel(selectedEmergencyFeature.report_type)}</p>
                        <p className="text-stone-600">{selectedEmergencyFeature.description}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className={getEmergencySeverityBadgeClass(selectedEmergencyFeature.severity)}>
                          {getEmergencySeverityLabel(selectedEmergencyFeature.severity)}
                        </Badge>
                        <Badge className={getEmergencyReportStatusBadgeClass(selectedEmergencyFeature.status)}>
                          {getEmergencyReportStatusLabel(selectedEmergencyFeature.status)}
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        className="w-full bg-orange-600 hover:bg-orange-500"
                        disabled={emergencyRouteLoading}
                        onClick={() => generateEmergencyRoute({ reportId: selectedEmergencyFeature.id })}
                      >
                        {emergencyRouteLoading ? "Routing..." : "Route To Help"}
                      </Button>
                    </>
                  )}
                </div>
              </Popup>
            )}
            {placingEmergencyReportPin && currentPos && (
              <Marker longitude={Number(currentPos.lng)} latitude={Number(currentPos.lat)} anchor="bottom">
                <div className="rounded-full border border-orange-200 bg-orange-500 px-2 py-1 text-[10px] font-semibold text-black shadow-[0_0_18px_rgba(249,115,22,0.5)]">
                  Tap map to drop pin
                </div>
              </Marker>
            )}
          </MapView>

          <div className="pointer-events-none absolute inset-0">
            <div
              className="pointer-events-auto absolute left-1/2 top-3 sm:top-4 w-[min(94vw,860px)]"
              style={{ transform: `translate(calc(-50% + ${bannerOffset.x}px), ${bannerOffset.y}px)` }}
            >
            <div className="rounded-xl border border-stone-700/70 bg-black/80 p-3 backdrop-blur">
              <div
                className="mb-2 -mt-1 flex items-center justify-center gap-1 text-[11px] text-stone-400 select-none cursor-grab active:cursor-grabbing touch-none"
                onPointerDown={beginBannerDrag}
                onPointerMove={moveBannerDrag}
                onPointerUp={endBannerDrag}
                onPointerCancel={endBannerDrag}
                title="Drag navigation banner"
                style={{ touchAction: "none", WebkitUserSelect: "none", userSelect: "none" }}
              >
                <GripHorizontal className="w-3.5 h-3.5" />
                <span>Drag</span>
              </div>
              {!isNavigating ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 space-y-2">
                    <div className="relative">
                      <Input
                        value={destinationQuery}
                        onChange={(e) => setDestinationQuery(e.target.value)}
                        placeholder="Search nearby businesses, places, or addresses"
                        className="bg-stone-900/90 border-stone-700"
                      />
                      {(destinationQuery.trim().length >= 2 || suggestions.length > 0 || searchLoading || searchError) && (
                        <div className="absolute z-20 mt-1 w-full rounded-md border border-stone-700 bg-stone-950/95 shadow-lg max-h-72 overflow-auto">
                          {searchLoading && (
                            <div className="px-3 py-3 text-sm text-stone-300">Searching nearby places...</div>
                          )}
                          {!searchLoading && suggestions.map((item) => (
                            <button
                              type="button"
                              key={item.id}
                              className="w-full border-b border-stone-800/70 px-3 py-2.5 text-left hover:bg-stone-800 last:border-b-0"
                              onClick={() => selectDestination(item)}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium text-stone-100">{item.primaryLabel}</p>
                                  <p className="truncate text-xs text-stone-400">
                                    {[item.secondaryLabel, item.categoryLabel].filter(Boolean).join(" · ") || item.place_name}
                                  </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  {Number.isFinite(item.distanceMiles) && (
                                    <span className="text-[11px] font-medium text-orange-300">
                                      {formatSearchDistance(item.distanceMiles)}
                                    </span>
                                  )}
                                  <Badge variant="outline" className="border-stone-700 bg-stone-900/80 text-[10px] text-stone-200">
                                    {item.featureTypeLabel}
                                  </Badge>
                                </div>
                              </div>
                            </button>
                          ))}
                          {!searchLoading && searchError && (
                            <div className="px-3 py-3 text-sm text-stone-400">{searchError}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {GPS_QUICK_SEARCHES.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => runQuickSearch(preset.query)}
                          className="rounded-full border border-stone-700 bg-stone-900/80 px-2.5 py-1 text-[11px] font-medium text-stone-200 transition hover:border-orange-500/60 hover:text-orange-200"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-stone-300">
                      <span className="flex items-center gap-1"><RouteIcon className="w-3.5 h-3.5 text-orange-400" /> {shownDistance} {distanceLabel}</span>
                      <span className="flex items-center gap-1"><Clock3 className="w-3.5 h-3.5 text-orange-400" /> ETA {formatEta(etaSeconds)}</span>
                      <span>Speed {Number(speedMph || 0).toFixed(1)} mph</span>
                      <span className="capitalize">{tripStatus}</span>
                    </div>
                    <div className="h-2 w-full rounded bg-stone-800 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-600 to-amber-500" style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }} />
                    </div>
                  </div>
                  <Button size="icon" variant="outline" className="border-stone-700 bg-black/70" onClick={() => setOpen(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{nextTurn || "Continue on current route"}</p>
                    <p className="text-xs text-stone-300 mt-1">
                      ETA {formatEta(etaSeconds)} · {shownDistance} {distanceLabel} · {Number(speedMph || 0).toFixed(1)} mph
                    </p>
                  </div>
                  <Button size="icon" variant="outline" className="border-stone-700 bg-black/70" onClick={() => setOpen(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            </div>
          </div>

          {isNavigating && controlsHidden && (
            <div className="pointer-events-none absolute inset-x-0 bottom-3 px-3">
              <div className="pointer-events-auto flex justify-center">
                <Button size="sm" className="bg-black/80 border border-stone-600 text-stone-100" onClick={() => setControlsHidden(false)}>
                  Show Navigation Controls
                </Button>
              </div>
            </div>
          )}

          {!controlsHidden && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 p-3 sm:p-4">
            <div className="pointer-events-auto rounded-xl border border-stone-700/70 bg-black/85 p-3 backdrop-blur space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className="font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-400" /> {nextTurn || "Start navigation to get turn guidance"}</p>
                <Button size="sm" variant="outline" className="border-stone-700 bg-black/60" onClick={recenter}>
                  <LocateFixed className="w-4 h-4 mr-1" /> Recenter
                </Button>
                <Button size="sm" variant="outline" className="border-stone-700 bg-black/60" onClick={() => { setLockToHeading(true); manualCameraUntilRef.current = 0; }}>
                  Align Heading
                </Button>
                {isNavigating && (
                  <Button size="sm" variant="outline" className="border-stone-700 bg-black/60" onClick={() => setControlsHidden(true)}>
                    Hide UI
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {tripStatus !== "active" ? (
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-500" onClick={tripStatus === "paused" ? resumeTrip : startTrip} disabled={!destination || !currentPos}>
                    <Play className="w-4 h-4 mr-1" /> {tripStatus === "paused" ? "Resume" : "Start"}
                  </Button>
                ) : (
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-500" onClick={pauseTrip}>
                    <Pause className="w-4 h-4 mr-1" /> Pause
                  </Button>
                )}
                <Button size="sm" variant="outline" className="border-red-500/50 text-red-300" onClick={endTrip} disabled={tripStatus === "idle" || tripStatus === "ended"}>
                  <Square className="w-4 h-4 mr-1" /> End
                </Button>
                <Button size="sm" variant="outline" className="border-stone-700" onClick={() => setMuted((v) => !v)}>
                  {muted ? <VolumeX className="w-4 h-4 mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />} {muted ? "Unmute" : "Mute"}
                </Button>
                <Button size="sm" variant="outline" className="border-stone-700" onClick={() => setSettingsOpen((v) => !v)}>
                  <Settings2 className="w-4 h-4 mr-1" /> Settings
                </Button>
                <div className="hidden sm:block rounded border border-stone-700 px-2 py-1 text-xs text-stone-300 text-center">
                  {statusText}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="border-orange-500/40 bg-orange-500/10 text-orange-100" onClick={() => setEmergencyPanelOpen((v) => !v)}>
                  <AlertTriangle className="w-4 h-4 mr-1" /> Emergency {emergencyPanelOpen ? "Hide" : "Tools"}
                </Button>
                <Button size="sm" variant="outline" className="border-orange-500/40 bg-black/60" onClick={() => setReportDialogOpen(true)}>
                  Report Incident
                </Button>
                {activeEmergencyRoute?.route_id && (
                  <Badge variant="outline" className="border-orange-500/40 bg-orange-500/10 text-orange-50">
                    Safe {safeRouteMode ? "ON" : "OFF"} · {formatEmergencyEta(activeEmergencyRoute.duration_seconds)}
                  </Badge>
                )}
              </div>

              {emergencyPanelOpen && (
                <div className="rounded-lg border border-orange-500/30 bg-stone-950/80 overflow-hidden">
                  <div className="flex items-start justify-between gap-3 border-b border-orange-500/20 px-3 py-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-orange-300">Emergency Services</p>
                      <p className="text-sm text-stone-300">Route to nearby care, law enforcement, shelter, and response services while keeping incident overlays visible.</p>
                      <p className="mt-1 text-[11px] text-stone-500">Scroll this panel on mobile. Drag the GPS banner to reposition it.</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge className="border-stone-700 bg-black/40 text-stone-100">Services {filteredEmergencyServices.length}</Badge>
                      <Badge className="border-stone-700 bg-black/40 text-stone-100">Reports {activeEmergencyReports.length}</Badge>
                      <Button size="icon" variant="outline" className="h-8 w-8 border-stone-700 bg-black/60" onClick={() => setEmergencyPanelOpen(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-[52vh] overflow-y-auto overscroll-contain px-3 py-3 pr-2 space-y-3 touch-pan-y">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                    <Select value={emergencyServiceFilter} onValueChange={setEmergencyServiceFilter}>
                      <SelectTrigger className="border-stone-700 bg-black/40">
                        <SelectValue placeholder="Service type" />
                      </SelectTrigger>
                      <SelectContent className="border-stone-700 bg-stone-950 text-stone-100">
                        <SelectItem value="all">All Services</SelectItem>
                        {EMERGENCY_SERVICE_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={emergencyStatusFilter} onValueChange={setEmergencyStatusFilter}>
                      <SelectTrigger className="border-stone-700 bg-black/40">
                        <SelectValue placeholder="Service status" />
                      </SelectTrigger>
                      <SelectContent className="border-stone-700 bg-stone-950 text-stone-100">
                        <SelectItem value="all">All Statuses</SelectItem>
                        {EMERGENCY_SERVICE_STATUSES.map((status) => (
                          <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant={showEmergencyServices ? "default" : "outline"} onClick={() => setShowEmergencyServices((v) => !v)}>
                      {showEmergencyServices ? "Hide Services" : "Show Services"}
                    </Button>
                    <Button size="sm" variant={showEmergencyReports ? "default" : "outline"} onClick={() => setShowEmergencyReports((v) => !v)}>
                      {showEmergencyReports ? "Hide Reports" : "Show Reports"}
                    </Button>
                    <Button size="sm" variant="outline" className="border-stone-700" onClick={() => fetchEmergencyMapData({ force: true })} disabled={emergencyLoading}>
                      {emergencyLoading ? "Refreshing..." : "Refresh Data"}
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-2 rounded-md border border-stone-700 px-3 py-2 text-sm">
                      <span>Safe Route</span>
                      <Switch checked={safeRouteMode} onCheckedChange={setSafeRouteMode} />
                    </label>
                    <Button size="sm" className="bg-orange-600 hover:bg-orange-500" onClick={() => generateEmergencyRoute({ preference: "nearest" })} disabled={emergencyRouteLoading || !currentPos}>
                      {emergencyRouteLoading && emergencyRoutePreference === "nearest" ? "Routing..." : "Route Nearest"}
                    </Button>
                    <Button size="sm" variant="outline" className="border-orange-500/40 text-orange-100" onClick={() => generateEmergencyRoute({ preference: "fastest" })} disabled={emergencyRouteLoading || !currentPos}>
                      {emergencyRouteLoading && emergencyRoutePreference === "fastest" ? "Routing..." : "Route Fastest"}
                    </Button>
                  </div>

                  {emergencyError && (
                    <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">{emergencyError}</div>
                  )}
                  {emergencyRouteError && (
                    <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">{emergencyRouteError}</div>
                  )}

                  <div className="grid lg:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-stone-400">Nearby Services</p>
                      <div className="max-h-48 overflow-auto overscroll-contain space-y-2 pr-1">
                        {filteredEmergencyServices.slice(0, 8).map((service) => (
                          <button
                            key={service.id}
                            type="button"
                            className="w-full rounded-md border border-stone-700 bg-black/30 px-3 py-2 text-left hover:border-orange-500/40"
                            onClick={() => setSelectedEmergencyFeature({ ...service, feature_kind: "service" })}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-sm text-stone-100">{service.name}</p>
                              <Badge className={getEmergencyServiceBadgeClass(service.status)}>{getEmergencyServiceStatusLabel(service.status)}</Badge>
                            </div>
                            <p className="mt-1 text-xs text-stone-400">{getEmergencyServiceTypeLabel(service.service_type)} · {formatEmergencyDistance(service.distance_miles)}</p>
                          </button>
                        ))}
                        {filteredEmergencyServices.length === 0 && (
                          <p className="rounded-md border border-dashed border-stone-700 px-3 py-4 text-sm text-stone-400">No services matched the active filters.</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-stone-400">Live Incident Reports</p>
                      <div className="max-h-48 overflow-auto overscroll-contain space-y-2 pr-1">
                        {activeEmergencyReports.slice(0, 8).map((report) => (
                          <button
                            key={report.id}
                            type="button"
                            className="w-full rounded-md border border-stone-700 bg-black/30 px-3 py-2 text-left hover:border-orange-500/40"
                            onClick={() => setSelectedEmergencyFeature({ ...report, feature_kind: "report" })}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-sm text-stone-100">{getEmergencyReportTypeLabel(report.report_type)}</p>
                              <Badge className={getEmergencySeverityBadgeClass(report.severity)}>{getEmergencySeverityLabel(report.severity)}</Badge>
                            </div>
                            <p className="mt-1 text-xs text-stone-400">{report.description}</p>
                          </button>
                        ))}
                        {activeEmergencyReports.length === 0 && (
                          <p className="rounded-md border border-dashed border-stone-700 px-3 py-4 text-sm text-stone-400">No active incidents reported nearby.</p>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              )}

              <div className="hidden lg:grid grid-cols-2 gap-2 max-h-36 overflow-auto pr-1">
                {(steps || []).slice(0, 6).map((step, idx) => (
                  <div key={`${step.maneuver?.instruction}-${idx}`} className="rounded-md border border-stone-700 p-2 text-sm">
                    <p className="text-stone-100">{step.maneuver?.instruction || "Continue"}</p>
                    <p className="text-xs text-stone-400 mt-1">{toMiles(step.distance)} mi · {formatEta(step.duration)}</p>
                  </div>
                ))}
              </div>

              {settingsOpen && (
                <div className="rounded-lg border border-stone-700 bg-stone-950/65 text-sm overflow-hidden">
                  <div className="flex items-center justify-between gap-2 border-b border-stone-700/70 px-3 py-3">
                    <p className="text-xs uppercase tracking-wider text-orange-300">Navigation Settings</p>
                    <Button size="icon" variant="outline" className="h-8 w-8 border-stone-700 bg-black/60" onClick={() => setSettingsOpen(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="max-h-[42vh] overflow-y-auto overscroll-contain px-3 py-3 space-y-3 touch-pan-y">
                  <label className="flex items-center justify-between gap-2"><span>Voice Guidance</span><Switch checked={voiceEnabled} onCheckedChange={setVoiceEnabled} /></label>
                  <label className="flex items-center justify-between gap-2"><span>Avoid Tolls</span><Switch checked={avoidTolls} onCheckedChange={setAvoidTolls} /></label>
                  <label className="flex items-center justify-between gap-2"><span>Avoid Highways</span><Switch checked={avoidHighways} onCheckedChange={setAvoidHighways} /></label>
                  <label className="flex items-center justify-between gap-2"><span>Auto Recenter</span><Switch checked={autoRecenter} onCheckedChange={setAutoRecenter} /></label>
                  <label className="flex items-center justify-between gap-2"><span>Follow Mode</span><Switch checked={followMode} onCheckedChange={setFollowMode} /></label>
                  <label className="flex items-center justify-between gap-2"><span>Show Truck Stops</span><Switch checked={showTruckStops} onCheckedChange={setShowTruckStops} /></label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant={units === "mi" ? "default" : "outline"} onClick={() => setUnits("mi")}>Miles</Button>
                    <Button size="sm" variant={units === "km" ? "default" : "outline"} onClick={() => setUnits("km")}>Kilometers</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant={nightMode ? "default" : "outline"} onClick={() => setNightMode(true)}><Moon className="w-4 h-4 mr-1" />Night</Button>
                    <Button size="sm" variant={!nightMode ? "default" : "outline"} onClick={() => setNightMode(false)}><Sun className="w-4 h-4 mr-1" />Day</Button>
                  </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
          <EmergencyReportDialog
            open={reportDialogOpen}
            onOpenChange={(value) => {
              setReportDialogOpen(value);
              if (!value) setPlacingEmergencyReportPin(false);
            }}
            form={emergencyReportForm}
            onChange={updateEmergencyReportForm}
            onSubmit={submitEmergencyReport}
            submitting={reportSubmitting}
            currentCoords={currentPos ? { latitude: currentPos.lat, longitude: currentPos.lng } : null}
            selectedCoords={reportPinCoords}
            placingOnMap={placingEmergencyReportPin}
            onUseCurrentLocation={useCurrentLocationForReport}
            onStartMapPick={() => {
              setPlacingEmergencyReportPin(true);
              setReportDialogOpen(false);
              toast.message("Tap the map to place the emergency incident");
            }}
          />
        </div>
      )}
    </>
  );
};

export default DriverNavigationConsole;
